import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Download, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

export default function CNAB() {
  const [bancos, setBancos] = useState([]);
  const [bancoId, setBancoId] = useState("");
  const [boletos, setBoletos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/bancos").then(({ data }) => setBancos(data.filter((b) => b.ativo)));
  }, []);

  useEffect(() => {
    if (!bancoId) { setBoletos([]); return; }
    api.get("/boletos", { params: { status: "EMITIDO" } }).then(({ data }) => {
      setBoletos(data.filter((b) => b.banco_id === bancoId));
      setSelected([]);
    });
  }, [bancoId]);

  const gerarRemessa = async () => {
    if (!bancoId) return toast.error("Selecione um banco");
    setBusy(true);
    try {
      const resp = await api.post(
        "/cnab/remessa",
        { banco_id: bancoId, boletos: selected.length ? selected : null },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `REMESSA_${Date.now()}.REM`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Arquivo de remessa gerado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar remessa");
    } finally { setBusy(false); }
  };

  const importarRetorno = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/cnab/retorno", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.aplicados} boletos atualizados de ${data.eventos} eventos`);
    } catch (err) {
      toast.error("Erro ao processar retorno");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const toggleSelect = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  return (
    <div className="space-y-6" data-testid="cnab-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Conciliação</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">CNAB 240</h1>
        <p className="text-sm text-zinc-500 mt-1">Gere arquivos de remessa e importe retornos bancários.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Remessa */}
        <Card className="rounded-sm border border-zinc-200 shadow-none p-6 space-y-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
            <Download className="w-3.5 h-3.5" /> Remessa (Hub → Banco)
          </div>
          <p className="text-sm text-zinc-600">Selecione o banco e (opcional) marque boletos específicos. Sem seleção, exporta TODOS os EMITIDO.</p>

          <Select value={bancoId} onValueChange={setBancoId}>
            <SelectTrigger className="rounded-sm h-10" data-testid="cnab-banco-select"><SelectValue placeholder="Selecione um banco…" /></SelectTrigger>
            <SelectContent>
              {bancos.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_code} – {b.banco_nome}</SelectItem>)}
            </SelectContent>
          </Select>

          {bancoId && (
            <div className="border border-zinc-200 rounded-sm max-h-80 overflow-y-auto">
              {boletos.length === 0 ? (
                <div className="text-sm text-zinc-500 p-6 text-center">Nenhum boleto EMITIDO para este banco.</div>
              ) : (
                <table className="tbl text-xs" data-testid="cnab-boletos-table">
                  <thead><tr><th className="w-8"></th><th>Nº Doc</th><th>Cliente</th><th className="text-right">Valor</th><th>Vencto</th></tr></thead>
                  <tbody>
                    {boletos.map((b) => (
                      <tr key={b.id} className="cursor-pointer" onClick={() => toggleSelect(b.id)} data-testid={`cnab-row-${b.id}`}>
                        <td><input type="checkbox" checked={selected.includes(b.id)} onChange={() => {}} /></td>
                        <td className="font-mono">{b.numero_documento}</td>
                        <td>{b.cliente?.nome}</td>
                        <td className="text-right font-mono tabular">R$ {b.valor.toFixed(2)}</td>
                        <td className="font-mono">{b.vencimento}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 items-center justify-between text-xs text-zinc-500">
            <span>{selected.length > 0 ? `${selected.length} selecionado(s)` : "Todos EMITIDO serão exportados"}</span>
            <Button onClick={gerarRemessa} disabled={busy || !bancoId} className="rounded-sm" data-testid="cnab-gerar-remessa">
              <Download className="w-4 h-4 mr-2" /> Gerar arquivo .REM
            </Button>
          </div>
        </Card>

        {/* Retorno */}
        <Card className="rounded-sm border border-zinc-200 shadow-none p-6 space-y-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
            <Upload className="w-3.5 h-3.5" /> Retorno (Banco → Hub)
          </div>
          <p className="text-sm text-zinc-600">Selecione o arquivo <code>.RET</code> ou <code>.TXT</code> exportado do Internet Banking para conciliação automática.</p>
          <label className="block border-2 border-dashed border-zinc-300 rounded-sm p-8 text-center cursor-pointer hover:border-zinc-500 transition-colors" data-testid="cnab-upload-label">
            <FileText className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
            <div className="text-sm">Clique ou arraste o arquivo CNAB 240 retorno aqui</div>
            <div className="text-[11px] text-zinc-500 mt-1 font-mono">.RET / .TXT</div>
            <input type="file" accept=".ret,.txt,.RET,.TXT" className="hidden" onChange={importarRetorno} data-testid="cnab-upload-input" />
          </label>

          {result && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm space-y-2" data-testid="cnab-result">
              <div className="flex justify-between"><span>Eventos lidos</span><span className="font-mono font-semibold">{result.eventos}</span></div>
              <div className="flex justify-between"><span>Boletos atualizados</span><span className="font-mono font-semibold text-emerald-700">{result.aplicados}</span></div>
              {result.amostra && result.amostra.length > 0 && (
                <details className="text-xs mt-2">
                  <summary className="cursor-pointer text-zinc-500">Ver amostra de eventos</summary>
                  <pre className="mt-2 bg-white border border-zinc-200 p-2 rounded-sm overflow-x-auto text-[10px]">{JSON.stringify(result.amostra, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
